// ─── StakeSelectionModal.jsx ──────────────────────────────────────────────────
// Glow Bingo — "Choose Your Entry Stake" bottom-sheet modal
// Designed as a POPUP on top of the Game page (GlowBingo.jsx).
// No Tailwind. All tokens from theme.js.
//
// USAGE in GlowBingo.jsx:
//   import StakeSelectionModal from "./StakeSelectionModal";
//   const [showStake, setShowStake] = useState(false);
//   ...
//   <StakeSelectionModal open={showStake} onClose={() => setShowStake(false)} />
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from "react";
import {
  X,
  Zap,
  Wallet,
  CheckCircle2,
  Lock,
  Flame,
  Sparkles,
  Star,
} from "lucide-react";
import { theme, injectTheme } from "../../assets/theme";

// ─── Stake options data ───────────────────────────────────────────────────────

const STAKES = [
  {
    id: "starter",
    amount: "10",
    unit: "BIRR",
    label: "Starter Entry",
    icon: Wallet,
    iconBg: "rgba(255,255,255,0.06)",
    iconColor: theme.color.textMuted,
    selected: false,
    locked: false,
    badge: null,
  },
  {
    id: "pro",
    amount: "20",
    unit: "BIRR",
    label: "Pro Choice",
    icon: Flame,
    iconBg: theme.color.accent,
    iconColor: "#fff",
    selected: true, // default selected
    locked: false,
    badge: null,
  },
  {
    id: "highroller",
    amount: "50",
    unit: "BIRR",
    label: "High Roller",
    icon: Star,
    iconBg: "rgba(255,255,255,0.06)",
    iconColor: theme.color.textMuted,
    selected: false,
    locked: false,
    badge: "2 Bingo",
  },
];

// ─── Inject extra keyframes only once ────────────────────────────────────────

function injectModalStyles() {
  if (document.getElementById("stake-modal-styles")) return;
  const s = document.createElement("style");
  s.id = "stake-modal-styles";
  s.textContent = `
    @keyframes slideUp {
      from { transform: translateY(100%); opacity: 0; }
      to   { transform: translateY(0);    opacity: 1; }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes popIn {
      0%   { transform: scale(0.94) translateY(16px); opacity: 0; }
      60%  { transform: scale(1.02) translateY(-2px); opacity: 1; }
      100% { transform: scale(1)    translateY(0);    opacity: 1; }
    }
  `;
  document.head.appendChild(s);
}

// ─── StakeCard ────────────────────────────────────────────────────────────────

function StakeCard({ stake, selected, onSelect, delay }) {
  const [hovered, setHovered] = useState(false);
  const { icon: Icon } = stake;
  const isActive = selected === stake.id;

  return (
    <button
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onSelect(stake.id)}
      aria-label={`Select ${stake.label} — ${stake.amount} ${stake.unit}`}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "16px 18px",
        borderRadius: theme.radius.lg,
        cursor: "pointer",
        border: isActive
          ? `1.5px solid ${theme.color.accent}`
          : `1px solid ${hovered ? theme.color.borderHover : theme.color.borderCard}`,
        background: isActive
          ? `linear-gradient(135deg, rgba(249,115,22,0.16) 0%, rgba(217,119,6,0.10) 100%)`
          : hovered
            ? theme.color.bgCardHover
            : theme.color.bgCard,
        boxShadow: isActive
          ? `0 0 0 1px ${theme.color.accentRing}, 0 8px 32px rgba(249,115,22,0.18)`
          : "none",
        transition: theme.transition.base,
        textAlign: "left",
        position: "relative",
        overflow: "hidden",
        animation: "popIn 0.38s ease both",
        animationDelay: `${delay}s`,
      }}
    >
      {/* Subtle glow sweep for active */}
      {isActive && (
        <span
          style={{
            position: "absolute",
            top: "-40px",
            right: "-30px",
            width: "110px",
            height: "110px",
            borderRadius: theme.radius.full,
            background: "rgba(249,115,22,0.10)",
            filter: "blur(28px)",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Icon */}
      <div
        style={{
          width: "44px",
          height: "44px",
          borderRadius: theme.radius.md,
          flexShrink: 0,
          background: isActive
            ? `linear-gradient(135deg, ${theme.color.playFrom}, ${theme.color.playTo})`
            : stake.iconBg,
          border: isActive ? "none" : `1px solid ${theme.color.borderCard}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: isActive ? `0 4px 16px rgba(249,115,22,0.40)` : "none",
          transition: theme.transition.base,
        }}
      >
        <Icon
          size={20}
          color={isActive ? "#fff" : stake.iconColor}
          fill={isActive ? "#fff" : "none"}
        />
      </div>

      {/* Amount + label */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "5px" }}>
          <span
            style={{
              fontFamily: theme.font.family,
              fontWeight: theme.font.weightBlack,
              fontSize: "26px",
              color: isActive ? theme.color.textPrimary : theme.color.textSub,
              lineHeight: 1,
              letterSpacing: "-0.01em",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {stake.amount}
          </span>
          <span
            style={{
              fontFamily: theme.font.family,
              fontWeight: theme.font.weightBold,
              fontSize: "13px",
              color: isActive ? theme.color.accentLight : theme.color.textDim,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            {stake.unit}
          </span>
        </div>
        <p
          style={{
            fontFamily: theme.font.bodyFamily,
            fontWeight: theme.font.weightMed,
            fontSize: "12px",
            color: isActive ? theme.color.textMuted : theme.color.textDim,
            marginTop: "3px",
          }}
        >
          {stake.label}
        </p>
      </div>

      {/* Right: badge or check */}
      <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
        {stake.badge && !isActive && (
          <span
            style={{
              fontSize: "10px",
              fontFamily: theme.font.bodyFamily,
              fontWeight: theme.font.weightBold,
              color: theme.color.accentLight,
              background: theme.color.accentMuted,
              border: `1px solid ${theme.color.accentRing}`,
              borderRadius: theme.radius.full,
              padding: "3px 9px",
              whiteSpace: "nowrap",
            }}
          >
            {stake.badge}
          </span>
        )}
        {isActive && (
          <div
            style={{
              width: "26px",
              height: "26px",
              borderRadius: theme.radius.full,
              background: theme.color.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 0 10px ${theme.color.accentGlow}`,
            }}
          >
            <CheckCircle2 size={15} color="#fff" fill="#fff" />
          </div>
        )}
      </div>
    </button>
  );
}

// ─── StakeSelectionModal ──────────────────────────────────────────────────────

export default function StakeSelectionModal({
  open,
  onClose,
  onConfirm,
  walletBalance = "450.00",
}) {
  const [selected, setSelected] = useState("pro");
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    injectTheme();
    injectModalStyles();
  }, []);

  // Handle open/close with animation timing
  useEffect(() => {
    if (open) {
      setVisible(true);
      requestAnimationFrame(() => setAnimating(true));
    } else {
      setAnimating(false);
      const t = setTimeout(() => setVisible(false), 320);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Close on Escape key
  const handleKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose?.();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  const handleConfirm = () => {
    onConfirm?.(selected);
    onClose?.();
  };

  if (!visible) return null;

  return (
    /* ── Full-screen backdrop ── */
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        background: animating ? "rgba(0,0,0,0.72)" : "rgba(0,0,0,0)",
        backdropFilter: animating ? "blur(6px)" : "blur(0px)",
        transition: "background 0.30s ease, backdrop-filter 0.30s ease",
      }}
    >
      {/* ── Sheet panel ────────────────────────────────────── */}
      <div
        style={{
          width: "100%",
          maxWidth: "430px",
          borderRadius: `${theme.radius.xxl} ${theme.radius.xxl} 0 0`,
          background: theme.color.bgSurface,
          border: `1px solid ${theme.color.border}`,
          borderBottom: "none",
          boxShadow: "0 -24px 80px rgba(0,0,0,0.85)",
          overflow: "hidden",
          transform: animating ? "translateY(0)" : "translateY(100%)",
          transition: "transform 0.34s cubic-bezier(0.32,0.72,0,1)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* ── Drag handle ─────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "14px 0 6px",
          }}
        >
          <span
            style={{
              width: "38px",
              height: "4px",
              borderRadius: theme.radius.full,
              background: "rgba(255,255,255,0.14)",
            }}
          />
        </div>

        {/* ── Wallet balance header ────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 22px 14px",
          }}
        >
          {/* Wallet info */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <div
              style={{
                width: "38px",
                height: "38px",
                borderRadius: theme.radius.md,
                background: `linear-gradient(135deg, ${theme.color.playFrom}, ${theme.color.playTo})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: theme.shadow.logo,
              }}
            >
              <Zap size={17} color="#fff" fill="#fff" />
            </div>
            <div>
              <p
                style={{
                  fontSize: "9px",
                  fontFamily: theme.font.bodyFamily,
                  fontWeight: theme.font.weightSemi,
                  color: theme.color.textDim,
                  textTransform: "uppercase",
                  letterSpacing: "0.16em",
                  marginBottom: "2px",
                }}
              >
                Play Wallet
              </p>
              <p
                style={{
                  fontFamily: theme.font.family,
                  fontWeight: theme.font.weightBlack,
                  fontSize: "18px",
                  color: theme.color.textPrimary,
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 1,
                  letterSpacing: "-0.01em",
                }}
              >
                {walletBalance}
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: theme.font.weightBold,
                    color: theme.color.accentLight,
                    marginLeft: "5px",
                    letterSpacing: "0.04em",
                  }}
                >
                  ETB
                </span>
              </p>
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: "34px",
              height: "34px",
              borderRadius: theme.radius.md,
              background: "rgba(255,255,255,0.06)",
              border: `1px solid ${theme.color.borderCard}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: theme.transition.base,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.11)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.06)";
            }}
          >
            <X size={16} color={theme.color.textMuted} />
          </button>
        </div>

        {/* ── Heading ──────────────────────────────────────── */}
        <div style={{ padding: "0 22px 10px" }}>
          <h1
            style={{
              fontFamily: theme.font.family,
              fontWeight: theme.font.weightBlack,
              fontSize: "28px",
              color: theme.color.textPrimary,
              lineHeight: 1.1,
              letterSpacing: "-0.01em",
              marginBottom: "2px",
            }}
          >
            Choose Your
          </h1>
          <h1
            style={{
              fontFamily: theme.font.family,
              fontWeight: theme.font.weightBlack,
              fontSize: "30px",
              fontStyle: "italic",
              background: `linear-gradient(90deg, ${theme.color.playFrom}, ${theme.color.playTo})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              lineHeight: 1.1,
              letterSpacing: "-0.01em",
              marginBottom: "10px",
            }}
          >
            Entry Stake
          </h1>
          <p
            style={{
              fontFamily: theme.font.bodyFamily,
              fontWeight: theme.font.weightReg,
              fontSize: "13px",
              color: theme.color.textDim,
              lineHeight: 1.55,
            }}
          >
            Higher stakes unlock rooms with massive prize pools and special
            multipliers.
          </p>
        </div>

        {/* ── Stake options ────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "10px 22px 18px",
          }}
        >
          {STAKES.map((stake, i) => (
            <StakeCard
              key={stake.id}
              stake={stake}
              selected={selected}
              onSelect={setSelected}
              delay={i * 0.07}
            />
          ))}
        </div>

        {/* ── Thin divider ─────────────────────────────────── */}
        <div
          style={{
            margin: "0 22px",
            height: "1px",
            background: `linear-gradient(to right, transparent, ${theme.color.borderDivide}, transparent)`,
          }}
        />

        {/* ── Action buttons ───────────────────────────────── */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "18px 22px 32px",
          }}
        >
          {/* Confirm CTA */}
          <button
            onClick={handleConfirm}
            style={{
              width: "100%",
              padding: "16px",
              borderRadius: theme.radius.lg,
              background: `linear-gradient(135deg, ${theme.color.playFrom}, ${theme.color.playTo})`,
              boxShadow: "0 6px 28px rgba(249,115,22,0.38)",
              border: "none",
              cursor: "pointer",
              fontFamily: theme.font.family,
              fontWeight: theme.font.weightBlack,
              fontSize: "15px",
              color: "#fff",
              letterSpacing: "0.10em",
              textTransform: "uppercase",
              transition: theme.transition.slow,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = theme.shadow.playHov;
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow =
                "0 6px 28px rgba(249,115,22,0.38)";
              e.currentTarget.style.transform = "none";
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = "scale(0.98)";
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
          >
            <Sparkles size={16} color="#fff" />
            Confirm & Enter Room
          </button>

          {/* Cancel */}
          <button
            onClick={onClose}
            style={{
              width: "100%",
              padding: "15px",
              borderRadius: theme.radius.lg,
              background: "rgba(255,255,255,0.04)",
              border: `1px solid ${theme.color.borderCard}`,
              cursor: "pointer",
              fontFamily: theme.font.family,
              fontWeight: theme.font.weightBold,
              fontSize: "14px",
              color: theme.color.textMuted,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              transition: theme.transition.base,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.08)";
              e.currentTarget.style.color = theme.color.textSub;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.04)";
              e.currentTarget.style.color = theme.color.textMuted;
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
